import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Html5Qrcode } from "html5-qrcode";
import {
  Camera, CheckCircle2, MapPin, Phone, FileText, Package,
  Upload, X, Loader2, ScanLine, ArrowRight, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { ViewInstruccionesButton } from "@/components/ViewInstruccionesButton";
import { ProyectoThumb } from "@/components/ProyectoThumb";

/** Página móvil-first para que el montador reporte un componente tras escanear su QR.
 * Botón GIGANTE "Reportar montaje".
 * Acepta también params en la URL: /r/c/:cid o /r/p/:pid (componente o petición).
 */
export default function Reportar() {
  const { cid, pid } = useParams();
  const nav = useNavigate();
  const isPet = !!pid;
  const id = pid || cid;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [marking, setMarking] = useState(false);
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    (async () => {
      try {
        const endpoint = isPet ? `/r/peticion/${id}` : `/r/componente/${id}`;
        const { data: d } = await api.get(endpoint);
        setData(d);
      } catch (e) {
        toast.error("No se encontró el QR escaneado");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isPet]);

  // ====== Scanner ======
  const startScan = async () => {
    setScanning(true);
    try {
      const qr = new Html5Qrcode("qr-scan-box", {
        verbose: false,
        useBarCodeDetectorIfSupported: true, // Iter 92 — detector nativo si está disponible
      });
      scannerRef.current = qr;
      await qr.start(
        { facingMode: "environment" },
        {
          fps: 25,
          qrbox: (vw, vh) => {
            const min = Math.min(vw, vh);
            const size = Math.min(380, Math.floor(min * 0.7));
            return { width: size, height: size };
          },
          aspectRatio: 1.333,
          disableFlip: false,
          videoConstraints: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            focusMode: "continuous",
            advanced: [{ focusMode: "continuous" }],
          },
        },
        (decoded) => {
          // decoded debería ser una URL tipo .../r/c/UUID o .../r/p/UUID
          const m = decoded.match(/\/r\/(c|p)\/([a-f0-9-]+)/i);
          if (m) {
            qr.stop().catch(() => {});
            setScanning(false);
            nav(`/r/${m[1]}/${m[2]}`);
          } else {
            toast.warning("QR no reconocido como código DNF");
          }
        },
        () => {}, // ignore errors per frame
      );
    } catch (e) {
      toast.error("No se pudo abrir la cámara: " + (e?.message || e));
      setScanning(false);
    }
  };

  const stopScan = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (_e) { /* ignore */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  // ====== Acciones de reporte ======
  const subirFoto = async (e, tipo = "final") => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // C1 — Estampar geolocalización en la foto si la petición lo tiene activado
      let fileToUpload = file;
      const geoOn = data?.peticion?.geolocalizar_fotos !== false;  // default true
      if (geoOn) {
        try {
          const { stampPhotoWithGeo } = await import("@/lib/photoGeo");
          // Jun-2026: sello con la ubicación del ESPACIO (si existe) y sin fecha
          const esp = data?.espacio;
          fileToUpload = await stampPhotoWithGeo(file, esp ? {
            direccion: esp.direccion || null,
            coords: (esp.latitud != null && esp.longitud != null)
              ? { latitud: esp.latitud, longitud: esp.longitud }
              : null,
          } : {});
        } catch (_e) { /* fallback al archivo original */ }
      }
      const fd = new FormData();
      fd.append("file", fileToUpload);
      fd.append("tipo", tipo);
      // Endpoint genérico de uploads (ya existe en server)
      const { data: r } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Asociar la foto al componente (campo foto_real)
      if (!isPet && data?.componente?.id) {
        await api.patch(`/componentes/${data.componente.id}`, {
          foto_real_url: r.url,
        });
        toast.success("Foto añadida al componente");
        // Reload
        const { data: nd } = await api.get(`/r/componente/${data.componente.id}`);
        setData(nd);
      } else {
        toast.success("Foto subida (" + r.url.split("/").pop() + ")");
      }
    } catch (err) {
      toast.error("Error subiendo: " + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const marcarInstalado = async () => {
    if (!data?.componente?.id) return;
    setMarking(true);
    try {
      await api.patch(`/componentes/${data.componente.id}`, { estado: "completado" });
      toast.success("Componente marcado como instalado ✓");
      const { data: nd } = await api.get(`/r/componente/${data.componente.id}`);
      setData(nd);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setMarking(false);
    }
  };

  // ====== Renders ======
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] text-zinc-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  // Pantalla "escanear" si no llegamos con cid/pid
  if (!data && !id) {
    return <ScanScreen onStart={startScan} scanning={scanning} onStop={stopScan} />;
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] text-zinc-100 flex flex-col items-center justify-center px-6">
        <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
        <div className="text-lg font-semibold">QR no encontrado</div>
        <div className="text-sm text-zinc-500 mt-1">La etiqueta podría estar duplicada o expirada.</div>
        <button onClick={startScan} className="mt-6 px-5 py-3 bg-amber-500 text-zinc-950 rounded-lg font-semibold">
          Escanear otro QR
        </button>
      </div>
    );
  }

  // Modal scanner abierto sobre la página
  if (scanning) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex flex-col">
        <div className="p-4 flex items-center justify-between">
          <div className="text-white font-semibold">Escanea el QR del componente</div>
          <button onClick={stopScan} className="text-white p-2"><X className="w-6 h-6" /></button>
        </div>
        <div id="qr-scan-box" className="flex-1" style={{ minHeight: 400 }}></div>
        <div className="p-4 text-center text-white/60 text-xs">Apunta a la etiqueta Brother con el QR. Se detecta automáticamente.</div>
      </div>
    );
  }

  const comp = data.componente;
  const peticion = data.peticion;
  const espacio = data.espacio;
  const cliente = data.cliente;
  const esCompletado = comp?.estado === "completado";
  const imgConsulta = comp?.imagen_url || peticion?.imagen_url || null;

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100 pb-32">
      {/* Header sticky */}
      <header className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-zinc-800 px-4 py-3 flex items-center gap-3">
        <img src="/brand/logo-rotulaciondnf.png" alt="" className="w-9 h-9 object-contain" />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-amber-400 uppercase tracking-wider">Reportar montaje</div>
          <div className="text-sm font-semibold truncate">{peticion?.codigo || "—"}</div>
        </div>
        {imgConsulta && (
          <ProyectoThumb url={imgConsulta} nombre={cliente?.nombre || ""} size={38} rounded="lg"
            data-testid="header-comp-imagen" />
        )}
        <button onClick={startScan}
          data-testid="btn-scan-other"
          className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-xs flex items-center gap-1">
          <ScanLine className="w-4 h-4" /> Otro
        </button>
      </header>

      <main className="px-4 py-5 space-y-4 max-w-2xl mx-auto">
        {/* Info clave */}
        <section className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 space-y-3">
          {comp && (
            <div className="flex items-start gap-3">
              <Package className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Componente</div>
                <div className="text-base font-semibold">{comp.descripcion}</div>
                <div className="text-xs text-zinc-500 mt-1">
                  {comp.cantidad} {comp.unidad || "ud"}
                  {comp.codigo ? ` · ${comp.codigo}` : ""}
                  {comp.estado ? ` · ${comp.estado}` : ""}
                </div>
              </div>
              {imgConsulta && (
                <div className="shrink-0 text-center">
                  <ProyectoThumb url={imgConsulta} nombre={cliente?.nombre || ""} size={72} rounded="lg"
                    data-testid="comp-imagen-thumb" />
                  <div className="text-[9px] text-zinc-500 mt-1">Toca para ampliar</div>
                </div>
              )}
            </div>
          )}
          {cliente && (
            <div className="flex items-start gap-3">
              <span className="text-amber-300 mt-0.5">🏢</span>
              <div className="flex-1">
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Cliente</div>
                <div className="text-base font-semibold">{cliente.nombre}</div>
                {cliente.telefono && (
                  <a href={`tel:${cliente.telefono}`} className="text-xs text-blue-400 inline-flex items-center gap-1 mt-1">
                    <Phone className="w-3 h-3" /> {cliente.telefono}
                  </a>
                )}
              </div>
            </div>
          )}
          {espacio && (
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Espacio</div>
                <div className="text-base font-semibold">{espacio.nombre}</div>
                {espacio.direccion && (
                  <a
                    href={`https://www.google.com/maps?q=${encodeURIComponent(espacio.direccion)}`}
                    target="_blank" rel="noreferrer"
                    className="text-xs text-blue-400 underline mt-1 inline-block"
                  >
                    {espacio.direccion} →
                  </a>
                )}
              </div>
            </div>
          )}
          {peticion?.descripcion && (
            <div className="flex items-start gap-3 pt-2 border-t border-zinc-800">
              <FileText className="w-5 h-5 text-zinc-500 mt-0.5 shrink-0" />
              <div className="text-sm text-zinc-300 whitespace-pre-wrap">{peticion.descripcion}</div>
            </div>
          )}
        </section>

        {/* Foto referencia (diseño) */}
        {comp?.foto_url && (
          <section>
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Referencia de diseño</div>
            <img src={comp.foto_url} alt="" className="w-full rounded-xl border border-zinc-800" />
          </section>
        )}

        {/* Foto real (lo que ha subido el montador) */}
        {comp?.foto_real_url && (
          <section>
            <div className="text-xs text-emerald-400 uppercase tracking-wider mb-2">✓ Foto subida</div>
            <img src={comp.foto_real_url} alt="" className="w-full rounded-xl border border-emerald-500/40" />
          </section>
        )}

        {/* Si es petición — listar componentes */}
        {isPet && data.componentes && data.componentes.length > 0 && (
          <section>
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">
              Componentes de la petición ({data.componentes.length})
            </div>
            <div className="space-y-2">
              {data.componentes.map((c) => (
                <button
                  key={c.id}
                  onClick={() => nav(`/r/c/${c.id}`)}
                  data-testid={`comp-${c.id}`}
                  className="w-full text-left bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-lg p-3 flex items-center gap-3"
                >
                  {c.imagen_url ? (
                    <ProyectoThumb url={c.imagen_url} nombre={c.descripcion || ""} size={34} rounded="lg"
                      data-testid={`comp-${c.id}-thumb`} />
                  ) : (
                    <Package className="w-4 h-4 text-amber-300 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{c.descripcion}</div>
                    <div className="text-[11px] text-zinc-500">{c.cantidad} {c.unidad || "ud"} · {c.estado || "pendiente"}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-600" />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Si es petición SIN componentes (solo montaje) — listar LÍNEAS como unidades reportables */}
        {isPet && data.lineas && data.lineas.length > 0 && (!data.componentes || data.componentes.length === 0) && (
          <section data-testid="reportar-lineas-section">
            <div className="text-xs text-amber-400 uppercase tracking-wider mb-2">
              📦 Solo montaje · {data.lineas.length} línea{data.lineas.length === 1 ? "" : "s"}
            </div>
            <div className="space-y-2">
              {data.lineas.map((l) => (
                <LineaReportCard key={l.id} linea={l} onUpdated={async () => {
                  try {
                    const { data: nd } = await api.get(`/r/peticion/${id}`);
                    setData(nd);
                  } catch (_e) { /* ignore */ }
                }} />
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Botones gigantes ANCLADOS abajo */}
      {!isPet && comp && (
        <div className="fixed bottom-0 inset-x-0 z-30 bg-gradient-to-t from-[#0a0a0c] via-[#0a0a0c]/95 to-transparent pt-6 pb-5 px-4">
          <div className="max-w-2xl mx-auto space-y-2.5">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => subirFoto(e, "final")}
              className="hidden"
              data-testid="file-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="btn-reportar"
              className="w-full py-5 bg-amber-500 hover:bg-amber-400 active:scale-[0.98] disabled:opacity-60 text-zinc-950 font-bold text-lg rounded-2xl flex items-center justify-center gap-3 shadow-2xl"
              style={{ boxShadow: "0 0 32px rgba(245, 158, 11, 0.4)" }}
            >
              {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Camera className="w-6 h-6" />}
              {uploading ? "SUBIENDO…" : "📸  REPORTAR / SUBIR FOTO"}
            </button>
            {!esCompletado && (
              <button
                onClick={marcarInstalado}
                disabled={marking}
                data-testid="btn-instalado"
                className="w-full py-3.5 bg-emerald-500 hover:bg-emerald-400 active:scale-[0.98] disabled:opacity-60 text-zinc-950 font-bold rounded-xl flex items-center justify-center gap-2"
              >
                {marking ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                Marcar instalado
              </button>
            )}
            {esCompletado && (
              <div className="w-full py-3.5 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 font-bold rounded-xl flex items-center justify-center gap-2">
                <CheckCircle2 className="w-5 h-5" /> Ya marcado como instalado
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LineaReportCard({ linea, onUpdated }) {
  const [working, setWorking] = useState(false);
  const completada = linea.estado === "completada" || linea.estado === "validada";

  const marcarCompletada = async () => {
    if (completada) return;
    if (!window.confirm("¿Marcar esta línea como completada?")) return;
    setWorking(true);
    try {
      await api.patch(`/lineas/${linea.id}`, {
        estado: "completada",
        fecha_realizada: new Date().toISOString(),
      });
      toast.success("Línea marcada como completada ✓");
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setWorking(false);
    }
  };

  const tipoLabel = (t) => {
    const map = {
      solo_montaje: "📦 Solo Montaje",
      produccion_montaje: "🏭 Producción + Montaje",
      diseno_produccion_montaje: "🎨 Diseño + Prod + Montaje",
      retirada: "♻️ Retirada",
    };
    return map[t] || t;
  };

  return (
    <div data-testid={`linea-card-${linea.id}`}
      className={`bg-zinc-900 border ${completada ? "border-emerald-500/40" : "border-zinc-800"} rounded-lg p-3`}>
      <div className="flex items-start gap-3">
        <Package className={`w-4 h-4 mt-0.5 shrink-0 ${completada ? "text-emerald-400" : "text-amber-300"}`} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{linea.descripcion || "—"}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            {tipoLabel(linea.tipo)} · {linea.estado || "pendiente"}
            {linea.fecha_programada && ` · ${new Date(linea.fecha_programada).toLocaleDateString("es-ES")}`}
          </div>
          <div className="mt-2">
            <ViewInstruccionesButton
              lineaId={linea.id}
              size="xs"
              variant="outline"
              testId={`view-instr-${linea.id}`}
            />
          </div>
        </div>
      </div>
      {!completada && (
        <button
          onClick={marcarCompletada}
          disabled={working}
          data-testid={`btn-completar-linea-${linea.id}`}
          className="w-full mt-3 py-2.5 bg-emerald-500 hover:bg-emerald-400 active:scale-[0.98] disabled:opacity-60 text-zinc-950 font-bold rounded-lg flex items-center justify-center gap-2"
        >
          {working ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Marcar línea como instalada
        </button>
      )}
      {completada && (
        <div className="w-full mt-3 py-2.5 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 font-bold rounded-lg flex items-center justify-center gap-2 text-sm">
          <CheckCircle2 className="w-4 h-4" /> Completada
        </div>
      )}
    </div>
  );
}

function ScanScreen({ onStart, scanning, onStop }) {
  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100 flex flex-col items-center justify-center px-6">
      <img src="/brand/logo-rotulaciondnf.png" alt="" className="w-20 h-20 object-contain mb-3" />
      <div className="text-2xl font-bold mb-2 text-amber-300">RotulaciónDNF</div>
      <div className="text-sm text-zinc-400 mb-8 text-center max-w-xs">
        Escanea el QR Brother de un componente o petición para reportar el montaje desde la obra.
      </div>
      <button
        onClick={scanning ? onStop : onStart}
        className="w-full max-w-xs py-5 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-lg rounded-2xl flex items-center justify-center gap-3 shadow-2xl"
        style={{ boxShadow: "0 0 32px rgba(245, 158, 11, 0.4)" }}
      >
        <ScanLine className="w-6 h-6" /> {scanning ? "DETENER" : "ESCANEAR QR"}
      </button>
    </div>
  );
}
